import React, { useState } from "react";
import { GridToolbar } from "@mui/x-data-grid";
import { DataGrid } from "@mui/x-data-grid";
import {
  Card,
  CardHeader,
  Button,
  CardContent,
  Box,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress
} from "@mui/material";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { useEffect } from "react";
import clsx from "clsx";
import { Download } from "@mui/icons-material";
import { post } from "../../services";
import { isDevelopment } from "../../Hooks/constants";

const UploadFiles = () => {
  const [tabledata, setTabledata] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dynamic Base URL based on Environment Variables
  const base = isDevelopment
    ? `${process.env.REACT_APP_SCRAP_SALE_DEV}`
    : `${process.env.REACT_APP_SCRAP_SALE_PROD}`;


  const HoverCell = ({ value }) => {
    return (
      <Tooltip title={value}>
        <div style={{ whiteSpace: "normal", textAlign: "start" }}>{value}</div>
      </Tooltip>
    );
  };

  const columns = [
    { field: "sl.no", headerName: "Sl.no", width: 70 },
    { field: "log_id", headerName: "LogId", width: 70 },
    {
      field: "file_name",
      headerName: "Files Name",
      width: 200,
      wrapText: true,
      align: "left",
      renderCell: (params) => {
        return <HoverCell value={params.value} />;
      },
    },
    {
      field: "created_at",
      headerName: "Date",
      width: 150,
      renderCell: (params) => {
        return <HoverCell value={params.value} />;
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      cellClassName: (params) => {
        if (params.value == null) {
          return "";
        }
        return clsx("super-app", {
          positive: params.value === "Success",
          negative: params.value === "Error",
          partial: params.value === "Partial Success",
          processing: params.value === "Processing",
        });
      },
    },
    {
      field: "count",
      headerName: (
        <div style={{ fontSize: "14px", lineHeight: "1.2" }}>
          <div>No of records </div>
          <div>processed</div>
        </div>
      ),
      width: 130,
      align: "center",
    },
    {
      field: "current_status",
      headerName: "Process Log",
      width: 200,
      renderCell: (params) => {
        return <HoverCell value={params.value} />;
      },
      cellClassName: (params) => {
        if (params.value == null) {
          return "";
        }

        return <HoverCell value={params.value} />;
      },
    },
    {
      field: "progress_id",
      headerName: "Progress",
      width: 90,
      wrapText: true,
      align: "left",
      renderCell: (params) => (
        <IconButton
          aria-label="Download"
          onClick={() => downloadProgressFile(params.row.log_id)}
        >
          <Download color="primary" />
        </IconButton>
      ),
    },
    {
      field: "documents_id",
      headerName: "Documents",
      width: 100,
      wrapText: true,
      align: "left",
      renderCell: (params) => (
        <IconButton
          aria-label="Download"
          onClick={() => downloadDocuments(params.row.log_id)}
        >
          <Download color="primary" />
        </IconButton>
      ),
    },
    {
      field: "customer_name",
      headerName: "Customer Name",
      width: 170,
      renderCell: (params) => {
        return <HoverCell value={params.value} />;
      },
    },
    {
      field: "customer_order_no",
      headerName: (
        <div style={{ fontSize: "14px", lineHeight: "1.2" }}>
          <div>Customer</div>
          <div>Order No</div>
        </div>
      ),
      width: 90,
      wrapText: true,
      align: "center",
    },

    {
      field: "draft_invoice",
      headerName: "Draft Invoice",
      width: 120,
      align: "center",
      renderCell: (params) => (
        <IconButton
          aria-label="Download"
          disabled={params.row.status !== "Success"}
          onClick={() =>
            downloadDraftInvoiceFile(
              params.row.log_id,
              params.row.customer_order_no
            )
          }
        >
          <Download
            color={params.row.status !== "Success" ? "grey" : "primary"}
          />
        </IconButton>
      ),
    },
    {
      field: "final_invoice",
      headerName: "Final Invoice",
      type: "number",
      width: 120,
      align: "center",
      renderCell: (params) => (
        <IconButton
          aria-label="Download"
          disabled={
            params.row.status !== "Success" ||
            !params.row.scrap_status ||
            params.row.scrap_status === null ||
            params.row.scrap_status !== "completed"
          }
          onClick={() =>
            downloadFinalInvoiceFile(
              params.row.log_id,
              params.row.customer_order_no
            )
          }
        >
          <Download
            color={
              params.row.status !== "Success" ||
                !params.row.scrap_status ||
                params.row.scrap_status === null ||
                params.row.scrap_status !== "completed"
                ? "grey"
                : "primary"
            }
          />
        </IconButton>
      ),
    },
  ];

  const downloadProgressFile = async (id) => {
    try {
      let response = await post(
        base,
        {
          rawPath: "/generate_scrap_data",
          type: "process_log",
          log_id: id,
        },
        true,
        false
      );
      const fileUrl = response?.body?.data || response?.data?.body?.data;
      if (response?.status === "success" || fileUrl) {
        window.open(fileUrl || response?.body?.data);
      } else {
        toast.error(
          response?.body?.message ||
          response?.data?.body?.message ||
          "Download Progress Failed",
          {
            position: "top-right",
          }
        );
      }
    } catch (error) {
      toast.error("Something went wrong", { position: "top-right" });
      console.error(error);
    }
  };

  const downloadDocuments = async (log_id) => {
    try {
      let response = await post(
        base,
        {
          rawPath: "/generate_scrap_data",
          type: "documents_data",
          log_id: log_id,
        },
        true,
        false
      );
      const fileUrl = response?.body?.data || response?.data?.body?.data;
      if (response?.status === "success" || fileUrl) {
        window.open(fileUrl || response?.body?.data);
      } else {
        toast.error(
          response?.body?.message ||
          response?.data?.body?.message ||
          "Download Documents Failed",
          {
            position: "top-right",
          }
        );
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong", { position: "top-right" });
    }
  };

  const downloadDraftInvoiceFile = async (log_id, customer_order_no) => {
    setLoading(true);
    let response = await post(
      base,
      {
        rawPath: "/draft_invoice",
        log_id: parseInt(log_id),
        customer_order_no: parseInt(customer_order_no),
      },
      true,
      false
    );
    const draftInvoiceUrl =
      response?.body?.draft_invoice_url ||
      response?.data?.body?.draft_invoice_url;
    if (draftInvoiceUrl) {
      setLoading(false);
      toast.success("Draft invoice dowloaded successfully", {
        position: "top-right",
      });
      window.open(draftInvoiceUrl, "_blank");
    } else {
      setLoading(false);
    }
  };

  const downloadFinalInvoiceFile = async (log_id, customer_order_no) => {
    try {
      setLoading(true);
      let response = await post(
        base,
        {
          rawPath: "/final_invoice",
          log_id: parseInt(log_id),
          customer_order_no: parseInt(customer_order_no),
        },
        true,
        false
      );
      const finalInvoiceUrl =
        response?.body?.final_invoice_url ||
        response?.data?.body?.final_invoice_url;
      if (response?.status === "success" || finalInvoiceUrl) {
        setLoading(false);
        toast.success("Final invoice dowloaded successfully", {
          position: "top-right",
        });
        window.open(finalInvoiceUrl || response?.body?.final_invoice_url, "_blank");
      } else {
        setLoading(false);
        toast.error(
          response?.body?.message ||
          response?.message ||
          "Failed to download final invoice",
          { position: "top-right" }
        );
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
      toast.error("Something went wrong", { position: "top-right" });
    }
  };

  const handleUploadData = async () => {
    try {
      setLoading(true);
      const response = await post(
        base,
        {
          rawPath: "/upload_details",
          module_name: "scrap_sale",
        },
        true,
        false
      );
      const rows = response?.body?.data || response?.data?.body?.data;
      if (rows) {
        const rowsData = rows.map((row, index) => ({
          ...row,
          "sl.no": index + 1,
        }));
        setTabledata(rowsData);
        setLoading(false);
      } else {
        toast.error(
          response?.body?.message || response?.message || "Fetch data failed",
          { position: "top-right" }
        );
        setLoading(false);
        return;
      }
    } catch (err) {
      console.log(err);
      toast.error("Something went wrong", { position: "top-right" });
      setLoading(false);
    }
  };

  useEffect(() => {
    handleUploadData();
  }, []);

  return (
    <>
      <Card
        sx={{
          backgroundColor: "#EEF2F7",
          padding: "1.3rem",
        }}
      >
        <CardHeader
          p={2}
          sx={{
            backgroundColor: "#F8F8F9",
            fontWeight: "700",
            borderTopRightRadius: "5px",
            borderTopLeftRadius: "5px",
          }}
          title={"Uploaded Files"}
          titleTypographyProps={{
            sx: {
              fontSize: "16px",
              fontWeight: "600",
              lineHeight: "16px",
              color: "#0160F1",
            },
          }}
          action={
            <>
              <Button onClick={() => window.location.reload()}>
                Refresh Page
              </Button>
            </>
          }
        />
        <Divider />
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 9999,
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        )}
        <CardContent
          sx={{
            backgroundColor: "#FFFFFF",
            borderBottomRightRadius: "5px",
            borderBottomLeftRadius: "5px",
          }}
        >
          <Box
            sx={{
              height: 700,
              width: "100%",
              "& .super-app.positive": {
                color: "rgb(4,171,105)",

                fontWeight: "600",
              },
              "& .super-app.negative": {
                color: "rgb(234,39,21)",

                fontWeight: "600",
              },
              "& .super-app.partial": {
                color: "rgb(244,161,0)",

                fontWeight: "600",
              },
              "& .super-app.progressdata": {
                color: "rgb(4,171,105)",

                fontWeight: "600",
              },
              "& .super-app.progressdatafail": {
                color: "rgb(234,39,21)",

                fontWeight: "600",
              },
              "& .super-app.progressdatapartial": {
                color: "rgb(244,161,0)",

                fontWeight: "600",
              },
              "& .super-app.processing": {
                color: "rgb(104,112,122)",

                fontWeight: "600",
              },
              "& .super-app.pending": {
                color: "rgb(3,95,240)",

                fontWeight: "600",
              },
            }}
          >
            <DataGrid
              rowHeight={80}
              showCellRightBorder={true}
              sx={{
                ".MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold !important",
                },
                overflowX: "scroll",
              }}
              rows={tabledata}
              columns={columns}
              headerClassName="header-center"
              rowsPerPageOptions={[10]}
              pageSize={10}
              getRowId={(row) => row.id}
              components={{
                Toolbar: GridToolbar,
              }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

export default UploadFiles;
